import {ClientUser, Item} from '../../../API';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../conflictResolution/types';
import {applyMerge} from "../applyMerge";

/**
 * Fields that should never be modified by merge (immutable or system-managed)
 */
const IMMUTABLE_FIELDS = ['id', 'ownerId', 'workspaceId', 'creator', 'createdAt', '__typename'];

/**
 * Fields that should always come from current state (system-managed)
 */
const SYSTEM_FIELDS = ['version', 'updatedAt', 'updatedBy', 'deletedAt', '_lastChangedAt'];

/**
 * Conflict handler for Item entities.
 * Handles version conflicts during item updates with intelligent merging.
 * 
 * Merge Strategy:
 * 1. Start with complete currentState (all server fields)
 * 2. Apply user's intendedChanges except for immutable/system fields
 *
 * Works for all field types (simple, arrays, JSON) without special handling.
 * 
 * CRITICAL: Item uses version field (DynamoDB convention), NOT version.
 * Only Task tables use version field.
 */
export const itemConflictHandler: ConflictHandler<Item> = {
    entityType: 'Item',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<Item> {
        // Fallback refetch - server should provide currentState
        throw new Error(
            'Item refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<Item>): Partial<Item> {
        const merged: Partial<Item> = {};

        // Start with current state
        Object.assign(merged, currentState);

        // Apply intended changes, respecting field categories
        applyMerge<Item>(merged, intendedChanges, IMMUTABLE_FIELDS, SYSTEM_FIELDS);

        // Ensure version is from current state (critical for retry)
        merged.version = currentState.version;

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
